import { useContext, useEffect, useState } from "react";
import { MemberContext } from "../Contexts/MemberContext";
import { useNavigate } from "react-router-dom";
import "./MemberList.scss";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { backGroundImage } from "../utils/backGroundImage";

const MemberList = () => {
  const navigate = useNavigate();
  const { currentUser, currentUserInfo, membersList, membersLoading } =
    useContext(MemberContext);

  const [displayImages, setDisplayImages] = useState([]);
  const [showNonMember, setShowNonMember] = useState(false);

  // ------------------------------------------------------------
  // 🔥 管理者権限（MemberContext を利用）
  // ------------------------------------------------------------
  const isAdmin = currentUserInfo?.administrator === true;

  // ------------------------------------------------------------
  // 🔥 スマホ遅延対策：読み込み中は判定しない
  // ------------------------------------------------------------
  useEffect(() => {
    if (membersLoading) return;

    // ❌ 未ログイン or 非会員
    if (!currentUser || !currentUserInfo) {
      alert("メンバー一覧は会員だけが閲覧できます。");
      navigate("/eventlist");
    }
  }, [membersLoading, currentUser, currentUserInfo, navigate]);

  // ------------------------------------------------------------
  // 🔥 背景画像
  // ------------------------------------------------------------
  useEffect(() => {
    backGroundImage(setDisplayImages);
  }, []);

  const displayImage = displayImages.length > 0 ? displayImages[0].src : "";

  // ------------------------------------------------------------
  // 🔐 閲覧可能なメンバーリスト（MemberContext の値を使用）
  // ------------------------------------------------------------
  const memberOnlyList = membersList.filter(
    (m) => !m.author?.id.includes("nonMember")
  );
  const nonMemberList = membersList.filter((m) =>
    m.author?.id.includes("nonMember")
  );

  // ------------------------------------------------------------
  // 🔥 設定ボタンの遷移
  // ------------------------------------------------------------
  const handleEdit = (id) => {
    if (currentUserInfo?.administrator || currentUserInfo?.author?.id === id) {
      navigate(`/membercreate`, { state: { authorId: id } });
    } else {
      alert("設定は本人と管理者だけが閲覧や編集できます。");
    }
  };

  const handleProfile = (id) => {
    if (!currentUserInfo) {
      alert("公開プロフィールは会員だけが閲覧できます。");
      navigate("/eventlist");
      return;
    }
    navigate(`/memberdetail`, { state: { authorId: id } });
  };

  // ------------------------------------------------------------
  // 🔥 削除処理（管理者のみ）
  // ------------------------------------------------------------
  const handleDelete = async (id) => {
    if (!isAdmin) return;

    const member = membersList.find((m) => m.author.id === id);
    if (!member) return alert("該当会員が見つかりません。");

    const ok = window.confirm(
      `本当に ${member.accountname} さんを削除しますか？`
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "members", member.id));

      // event_members からも削除
      const qEvent = query(
        collection(db, "event_members"),
        where("memberId", "==", id)
      );
      const snap = await getDocs(qEvent);
      for (const d of snap.docs) {
        await deleteDoc(d.ref);
      }

      alert("削除しました。");
    } catch (error) {
      console.error(error);
      alert("削除に失敗しました。");
    }
  };

  // ------------------------------------------------------------
  // 🔥 UI
  // ------------------------------------------------------------
  if (membersLoading || !currentUser || !currentUserInfo) {
    return (
      <div className="memberlist-loading">メンバー情報を読み込み中...</div>
    );
  }

  return (
    <div
      className="memberlist-container"
      style={{ backgroundImage: `url(${displayImage})` }}
    >
      <div className={`memberlist-field ${isAdmin ? "" : "member"}`}>
        <table className={`table_layout ${isAdmin ? "" : "member"}`}>
          <thead>
            <tr>
              {isAdmin && <th>お名前</th>}
              <th>アカウント名</th>
              {isAdmin && (
                <>
                  <th>メールアドレス</th>
                  <th>携帯番号</th>
                </>
              )}
              <th>公開プロフィール</th>
              {isAdmin && (
                <>
                  <th>参加承認</th>
                  <th>管理権限</th>
                </>
              )}
              <th>設定</th>
              {isAdmin && <th>削除</th>}
            </tr>
          </thead>

          <tbody>
            {memberOnlyList.map((member) => (
              <tr key={member.id}>
                {isAdmin && <td>{member.username}</td>}
                <td>{member.accountname}</td>

                {isAdmin && (
                  <>
                    <td>{member.email}</td>
                    <td>{member.tel_num}</td>
                  </>
                )}

                <td>
                  <button
                    className="memberlist-detail-profile-button"
                    onClick={() => handleProfile(member.author.id)}
                  >
                    会員情報
                  </button>
                </td>

                {isAdmin && (
                  <>
                    <td>{member.admin ? "■" : "□"}</td>
                    <td>{member.administrator ? "■" : "□"}</td>
                  </>
                )}

                <td>
                  <button
                    className="memberlist-detail-button"
                    onClick={() => handleEdit(member.author.id)}
                  >
                    設定
                  </button>
                </td>

                {isAdmin && (
                  <td>
                    <button
                      className="memberlist-delete-button"
                      onClick={() => handleDelete(member.author.id)}
                    >
                      削除
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* 非会員の表示 */}
            {isAdmin && (
              <tr>
                <td colSpan="10" style={{ textAlign: "right" }}>
                  <label style={{ marginRight: "8px" }}>会員外を表示</label>
                  <input
                    type="checkbox"
                    checked={showNonMember}
                    onChange={() => setShowNonMember((prev) => !prev)}
                  />
                </td>
              </tr>
            )}

            {isAdmin &&
              showNonMember &&
              nonMemberList.map((member) => (
                <tr key={member.id}>
                  <td>{member.username}</td>
                  <td>{member.accountname}</td>
                  <td>{member.email}</td>
                  <td>{member.tel_num}</td>
                  <td>
                    <button
                      className="memberlist-detail-profile-button"
                      onClick={() => handleProfile(member.author.id)}
                    >
                      会員情報
                    </button>
                  </td>
                  <td>{member.admin ? "■" : "□"}</td>
                  <td>{member.administrator ? "■" : "□"}</td>
                  <td>
                    <button
                      className="memberlist-detail-button"
                      onClick={() => handleEdit(member.author.id)}
                    >
                      設定
                    </button>
                  </td>
                  <td>
                    <button
                      className="memberlist-delete-button"
                      onClick={() => handleDelete(member.author.id)}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberList;
